import React from 'react';
import { Sparkles } from 'lucide-react';
import { VideoEffect, VideoEffectControlProps, VideoEffectCompositeArgs } from './VideoEffect';
import { ThumbTile } from './ThumbTile';

export type MakeupLevel = 'none' | 'light' | 'medium' | 'strong';
export interface MakeupConfig {
  level: MakeupLevel;
  /**
   * Bascule vers la version précise (repérage du visage par MediaPipe FaceLandmarker,
   * lissage en séparation de fréquences pour éviter l'effet plastique) — PAS ENCORE
   * IMPLÉMENTÉE. Le champ existe déjà pour que le jour où elle le sera, l'ajouter ne
   * demande qu'un nouveau branchement dans composite(), pas une refonte de la config
   * ni de la modale. Pour l'instant composite() ignore ce booléen : une seule version
   * (la simple, ci-dessous) tourne, quelle que soit sa valeur.
   */
  precise: boolean;
}

// Pas de tuile "Aucun" : ce choix est déjà porté par le bouton d'effet lui-même dans la
// rangée "Effets vidéo" (le désactiver là revient au niveau 'none', cf. defaultConfig) —
// la tuile ici faisait doublon (même correctif que SurroundingsEffect pour "Fond réel").
const MAKEUP_LEVELS: { id: MakeupLevel; label: string }[] = [
  { id: 'light',  label: 'Léger' },
  { id: 'medium', label: 'Moyen' },
  { id: 'strong', label: 'Fort' },
];

// Flou du lissage — plus doux que celui du fond (SurroundingsEffect, mode flou) :
// ici il ne s'applique QUE sur la peau détectée, un excès tourne vite au visage
// "plastique".
const SMOOTH_PX: Record<MakeupLevel, number> = { none: 0, light: 3, medium: 6, strong: 10 };
// Flou de démonstration sur l'icône des tuiles — même principe que SurroundingsEffect.
const PREVIEW_BLUR_PX: Record<MakeupLevel, number> = { none: 0, light: 1, medium: 2, strong: 3.5 };

// Résolution de travail pour la détection de teint — pas la peine de classer chaque
// pixel HD individuellement, un rééchantillonnage grossier suffit et l'agrandissement
// (drawImage en étirant) adoucit de lui-même les bords du masque.
const SKIN_MASK_W = 160;
const SKIN_MASK_H = 90;

// Canvases de travail, partagés entre tous les appelants de composite() (préview locale
// ET pipeline réel, cf. compositeFrame.ts) — un seul jeu, jamais recréé par frame.
const skinSampleCanvas = document.createElement('canvas');
const skinMaskCanvas   = document.createElement('canvas');
const smoothedLayerCanvas = document.createElement('canvas');
skinSampleCanvas.width = SKIN_MASK_W; skinSampleCanvas.height = SKIN_MASK_H;
skinMaskCanvas.width   = SKIN_MASK_W; skinMaskCanvas.height   = SKIN_MASK_H;

/**
 * Règle de classement "ce pixel ressemble-t-il à de la peau ?" — heuristique RGB
 * classique (Peer et al.), pas de modèle : rapide, imprécise sous éclairage inhabituel
 * ou sur certains teints, mais suffisante pour une version "simple, pour voir" — cf.
 * MakeupConfig.precise pour la version à base de repères du visage, à venir.
 */
function isSkinTone(r: number, g: number, b: number): boolean {
  const maxc = Math.max(r, g, b);
  const minc = Math.min(r, g, b);
  return r > 95 && g > 40 && b > 20
    && (maxc - minc) > 15
    && Math.abs(r - g) > 15
    && r > g && r > b;
}

/**
 * Construit le masque "peau ET personne" à résolution réduite : on échantillonne la
 * frame en petit, on classe chaque pixel, puis on l'intersecte avec le masque de
 * détourage déjà calculé (évite les faux positifs sur un mur ou un meuble couleur chair
 * dans le fond). Retourne le petit canvas — l'agrandissement se fait au drawImage final.
 */
function buildSkinMask(frame: HTMLVideoElement, personAlpha: HTMLCanvasElement): HTMLCanvasElement {
  const sctx = skinSampleCanvas.getContext('2d')!;
  sctx.drawImage(frame, 0, 0, SKIN_MASK_W, SKIN_MASK_H);
  const sample = sctx.getImageData(0, 0, SKIN_MASK_W, SKIN_MASK_H);
  const data   = sample.data;

  const mask = sctx.createImageData(SKIN_MASK_W, SKIN_MASK_H);
  const out  = mask.data;
  for (let i = 0; i < data.length; i += 4) {
    const skin = isSkinTone(data[i], data[i + 1], data[i + 2]);
    out[i] = out[i + 1] = out[i + 2] = 255;
    out[i + 3] = skin ? 255 : 0;
  }

  const mctx = skinMaskCanvas.getContext('2d')!;
  mctx.putImageData(mask, 0, 0);
  // Intersection avec le détourage personne — évite qu'un fond couleur chair ne se
  // fasse lisser lui aussi.
  mctx.globalCompositeOperation = 'destination-in';
  mctx.drawImage(personAlpha, 0, 0, SKIN_MASK_W, SKIN_MASK_H);
  mctx.globalCompositeOperation = 'source-over';

  return skinMaskCanvas;
}

class MakeupEffectImpl extends VideoEffect<MakeupConfig> {
  readonly id = 'makeup';
  readonly label = 'Makeup';
  readonly icon = Sparkles;
  readonly defaultConfig: MakeupConfig = { level: 'none', precise: false };
  readonly group = 'face' as const;
  readonly needsSegmentation = true;

  composite({ ctx, width, height, frame, personAlpha, config }: VideoEffectCompositeArgs<MakeupConfig>) {
    // Groupe "face" : ne repeint JAMAIS la base — elle est déjà posée (par l'effet actif
    // du groupe "surroundings", ou un simple passthrough) avant que ceci ne s'exécute.
    // Rien à faire tant que le niveau est "Aucun" ou qu'aucun masque personne n'est
    // encore disponible (premières frames) : la base déjà peinte suffit.
    const px = SMOOTH_PX[config.level];
    if (px <= 0 || !personAlpha) return;

    const skinMask = buildSkinMask(frame, personAlpha);

    smoothedLayerCanvas.width  = width;
    smoothedLayerCanvas.height = height;
    const lctx = smoothedLayerCanvas.getContext('2d')!;
    lctx.clearRect(0, 0, width, height);
    lctx.filter = `blur(${px}px)`;
    lctx.drawImage(frame, 0, 0, width, height);
    lctx.filter = 'none';
    lctx.globalCompositeOperation = 'destination-in';
    lctx.drawImage(skinMask, 0, 0, width, height);
    lctx.globalCompositeOperation = 'source-over';

    ctx.drawImage(smoothedLayerCanvas, 0, 0, width, height);
  }

  renderControls({ config, onChange }: VideoEffectControlProps<MakeupConfig>) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {MAKEUP_LEVELS.map(lvl => (
          <ThumbTile key={lvl.id} active={config.level === lvl.id} label={lvl.label} onClick={() => onChange({ ...config, level: lvl.id })}>
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles style={{ width: 26, height: 26, color: '#94a3b8', filter: `blur(${PREVIEW_BLUR_PX[lvl.id]}px)` }} />
            </div>
          </ThumbTile>
        ))}
      </div>
    );
  }
}

export const makeupEffect = new MakeupEffectImpl();
